/** @jsx jsx */
import * as React from 'react';
import {
  useCombobox,
  useMultipleSelection,
  UseMultipleSelectionProps,
} from 'downshift';
import { jsx, css } from '@emotion/core';
import cx from 'classnames';
import Highlighter from 'react-highlight-words';
import useOnClickOutside from 'use-onclickoutside';
import { OverflowList } from '@blueprintjs/core/lib/cjs/components/overflow-list/overflowList';
import { useDeepCompareEffect } from 'react-use';

import AddBlock from '@clearkit/icons/glyphs/AddBlock';
import CloseOrb from '@clearkit/icons/glyphs/CloseOrb';
import Caret from '@clearkit/icons/glyphs/Caret';

import { CKCheckbox } from '../CKCheckbox';
import { CKChip } from '../CKChip';
import { CKSelectListItem } from '../CKSelectListItem';
import { usePopover } from '../../hooks';
import {
  ItemBase,
  CommonSelectProps,
  CKPickerVariant,
} from '../../common/types';
import {
  defaultItemRenderer,
  defaultEmptyState,
  defaultOptionFilterFunc,
} from '../../common/utils';

export interface CKMultiPickerProps<T extends ItemBase>
  extends UseMultipleSelectionProps<T>,
    CommonSelectProps<T> {
  items: T[];
  isCreateable?: boolean;
  isDisabled?: boolean;
  isSmall?: boolean;
  variant?: CKPickerVariant;
  onCreateItem?: (item: T) => void;
  itemRenderer?: (item: T) => string;
  emptyState?: (inputValue: string) => React.ReactNode;
  optionFilterFunc?: (items: T[], inputValue: string) => T[];
}

export const CKMultiPicker = <T extends ItemBase>(
  props: CKMultiPickerProps<T>
): React.ReactElement<CKMultiPickerProps<T>> => {
  const {
    placeholder,
    leftIcon,
    itemRenderer = defaultItemRenderer,
    emptyState = defaultEmptyState,
    itemMetaRenderer = null,
    isItemDisabled = () => false,
    optionFilterFunc = defaultOptionFilterFunc,
    items,
    popperZIndex = 10,
    isCreateable,
    isDisabled = false,
    isSmall = false,
    onCreateItem,
    variant = 'default',
    ...downshiftProps
  } = props;
  const [isCreating, setIsCreating] = React.useState(false);
  const [inputValue, setInputValue] = React.useState('');
  const [inputItems, setInputItems] = React.useState<T[]>(items);
  const popperReference = React.useRef<HTMLDivElement>(null);
  const popperPopover = React.useRef<HTMLDivElement>(null);
  const popper = usePopover({
    popperReference,
    popperPopover,
    popperOffset: props.popperOffset,
    popperPlacement: props.popperPlacement,
    popperModifiers: props.popperModifiers,
  });

  const {
    getSelectedItemProps,
    getDropdownProps,
    addSelectedItem,
    removeSelectedItem,
    selectedItems,
  } = useMultipleSelection(downshiftProps);

  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    highlightedIndex,
    getItemProps,
    openMenu,
    closeMenu,
    selectItem,
    setHighlightedIndex,
  } = useCombobox({
    inputValue,
    selectedItem: undefined,
    items: inputItems,
    onInputValueChange: ({ inputValue, selectedItem }) => {
      const filteredItems = optionFilterFunc(items, inputValue || '');

      if (isCreateable && isCreating && filteredItems.length > 0) {
        setIsCreating(false);
      }

      if (!selectedItem) {
        setInputItems(filteredItems);
      }
    },
    stateReducer: (state, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            isOpen: true,
          };
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            highlightedIndex: state.highlightedIndex,
            isOpen: true,
          };
        default:
          return changes;
      }
    },
    // @ts-ignore
    onStateChange: ({ inputValue, type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputChange:
          setInputValue(inputValue || '');
          break;
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            if (selectedItemValues.includes(selectedItem.value)) {
              removeSelectedItem(selectedItem);
            } else {
              if (isCreateable && onCreateItem && isCreating) {
                onCreateItem(selectedItem);
                setIsCreating(false);
                setInputItems(items);
              } else {
                addSelectedItem(selectedItem);
              }
            }

            // @ts-ignore
            selectItem(null);
            setInputValue('');
          }
          break;
        default:
          break;
      }
    },
  });

  React.useEffect(() => {
    if (isOpen && popper.current) {
      popper.current?.forceUpdate();
    }
  }, [isOpen, popper, selectedItems]);

  React.useEffect(() => {
    if (inputItems.length === 0 && isCreateable) {
      setIsCreating(true);
      // @ts-ignore
      setInputItems([{ label: `${inputValue}`, value: inputValue }]);
      setHighlightedIndex(0);
    }
  }, [
    inputItems,
    isCreateable,
    setIsCreating,
    setHighlightedIndex,
    inputValue,
  ]);

  useDeepCompareEffect(() => {
    setInputItems(items);
  }, [items]);

  useOnClickOutside(popperReference, closeMenu);

  const outerPickerClass = cx('ck-picker', {
    'ck-picker--open': isOpen,
    'ck-picker--disabled': isDisabled,
    'ck-picker--small': isSmall,
    'ck-picker--error': variant === 'error',
    'ck-picker--success': variant === 'success',
  });

  const selectedItemValues = selectedItems.map(item => item.value);

  return (
    <div className={outerPickerClass} ref={popperReference}>
      <div
        {...getComboboxProps()}
        className="ck-picker-toggle ck-picker-toggle--typeable"
        css={css`
          min-height: 2.25rem;
        `}
      >
        <label className="sr-only" {...getLabelProps()}>
          {placeholder}
        </label>

        {leftIcon && (
          <span className="ck-picker-icon ck-picker-icon--left">
            {leftIcon}
          </span>
        )}

        {selectedItems.length === 0 && !isOpen && (
          <span
            className="ck-picker-value ck-picker-value--placeholder"
            css={css`
              position: relative;
              left: -2px;
            `}
          >
            {placeholder}
          </span>
        )}

        {!isOpen && (
          <OverflowList
            collapseFrom="end"
            className="ck-picker-values"
            css={css`
              display: flex;
              flex-wrap: nowrap;
              overflow: hidden;
              position: relative;
              flex: 1;

              .bp3-overflow-list-spacer {
                flex-shrink: 1;
                width: 1px;
                pointer-events: none;
              }
            `}
            items={selectedItems}
            overflowRenderer={items => {
              return (
                <div
                  css={css`
                    display: flex;
                    align-items: center;
                  `}
                >
                  <span
                    css={css`
                      pointer-events: none;
                    `}
                    className="ck-badge"
                  >
                    +{items.length} more
                  </span>
                </div>
              );
            }}
            visibleItemRenderer={(selectedItem, index) => {
              return (
                <CKChip
                  css={css`
                    z-index: 10;
                    ${isDisabled && 'pointer-events: none;'}
                  `}
                  key={`selected-item-${index}`}
                  {...getSelectedItemProps({
                    selectedItem,
                    index,
                    disabled: isDisabled,
                  })}
                  onClick={(e: any) => {
                    e.stopPropagation();
                    removeSelectedItem(selectedItem);
                  }}
                >
                  {selectedItem.label}
                </CKChip>
              );
            }}
          />
        )}

        <div className={isOpen ? 'ck-picker-values' : ''}>
          {isOpen &&
            selectedItems.map((selectedItem, index) => {
              return (
                <span
                  className="ck-chip"
                  key={`selected-item-${index}`}
                  {...getSelectedItemProps({ selectedItem, index })}
                >
                  {selectedItem.label}
                  <button
                    disabled={isDisabled}
                    type="button"
                    className="ck-chip-delete"
                    onClick={e => {
                      e.stopPropagation();
                      removeSelectedItem(selectedItem);
                    }}
                  >
                    <CloseOrb height={16} width={16} />
                  </button>
                </span>
              );
            })}
          <input
            disabled={isDisabled}
            className="ck-picker-value"
            css={
              isOpen
                ? css`
                    display: inline-block;
                    width: auto;
                  `
                : css`
                    position: absolute;
                    top: 0;
                    left: 0;
                    right: 0;
                    bottom: 0;
                    width: 100%;
                    height: 100%;
                    opacity: 0;
                  `
            }
            {...getInputProps(
              getDropdownProps({
                placeholder,
                onClick: isOpen ? () => {} : openMenu,
                onFocus: isOpen ? () => {} : openMenu,
              })
            )}
          />
        </div>

        <button
          disabled={isDisabled}
          css={css`
            display: flex;
            margin-left: auto;
          `}
          {...getToggleButtonProps({ type: 'button' })}
          aria-label="toggle menu"
        >
          <span className="ck-picker-icon ck-picker-icon--right">
            <Caret />
          </span>
        </button>
      </div>
      <div
        css={css`
          visibility: ${isOpen ? 'visible' : 'hidden'};
          pointer-events: ${isOpen ? 'auto' : 'none'};
          z-index: ${popperZIndex};
        `}
        {...getMenuProps({ ref: popperPopover })}
        className="ck-popover ck-popover--full-width"
      >
        <ul
          className="ck-popover-options"
          css={css`
            outline: 0;
          `}
        >
          {isOpen &&
            inputItems.length > 0 &&
            inputItems.map((item, index) => (
              <CKSelectListItem
                key={`${item}${index}`}
                isHighlighted={highlightedIndex === index}
                isSelected={selectedItemValues.includes(item.value)}
                isDisabled={isItemDisabled(item)}
                {...getItemProps({
                  item,
                  index,
                  disabled: isItemDisabled(item),
                })}
                itemMetaRenderer={
                  itemMetaRenderer ? itemMetaRenderer(item) : null
                }
                accessoryRenderer={
                  isCreating ? (
                    <AddBlock />
                  ) : (
                    <CKCheckbox
                      id={item.value}
                      checked={selectedItemValues.includes(item.value)}
                      value={item.value}
                      label={null}
                    />
                  )
                }
              >
                {isCreating ? (
                  <p>
                    <span>Create</span>{' '}
                    <span className="font-medium">{item.label}</span>
                  </p>
                ) : (
                  <Highlighter
                    autoEscape
                    searchWords={[inputValue || '']}
                    textToHighlight={itemRenderer(item)}
                    highlightClassName="ck-highlight"
                  ></Highlighter>
                )}
              </CKSelectListItem>
            ))}
          {isOpen && inputItems.length === 0 && emptyState(inputValue)}
        </ul>
      </div>
    </div>
  );
};
